import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  alpha,
  Skeleton,
  Grid,
} from '@mui/material';
import {
  CalendarToday,
  WarningAmber,
  Autorenew,
} from '@mui/icons-material';
import { PenLine } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setActiveSmartViewAction, setActiveWorkspace, setActiveFolder } from '../../../redux/actions/contractAction';

const GOLD = '#c9a96e';

function ActionCard({ icon: Icon, lucideIcon: LucideIcon, label, count, color, loading, onClick }) {
  const theme = useTheme();

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton variant="rectangular" height={48} sx={{ borderRadius: '8px' }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        borderRadius: '12px',
        border: `1px solid ${alpha(color, 0.2)}`,
        background: alpha(color, 0.03),
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: `0 4px 16px ${alpha(color, 0.12)}`,
          borderColor: alpha(color, 0.35),
          transform: 'translateY(-1px)',
        },
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: alpha(color, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {LucideIcon ? (
              <LucideIcon size={20} color={color} />
            ) : (
              <Icon sx={{ fontSize: 20, color }} />
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: '1.5rem',
                lineHeight: 1.2,
                color: theme.palette.text.primary,
              }}
            >
              {count ?? 0}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
                fontSize: '0.75rem',
                lineHeight: 1.3,
              }}
            >
              {label}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

const ActionRequiredRow = ({ smartViewCounts, sigPerformance, alertSummary }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSmartViewNav = (viewId) => {
    dispatch(setActiveSmartViewAction(viewId));
    dispatch(setActiveWorkspace(null));
    dispatch(setActiveFolder(null));
    navigate('/contract');
  };

  const cards = [
    {
      icon: CalendarToday,
      label: t('clmDashboard.expiringSoon', 'Expiring Soon'),
      count: smartViewCounts.data?.expiring_30,
      color: '#ef4444',
      loading: smartViewCounts.loading,
      onClick: () => handleSmartViewNav('expiring_30'),
    },
    {
      icon: Autorenew,
      label: t('clmDashboard.autoRenewalDeadlines', 'Auto-Renewal Deadlines'),
      count: smartViewCounts.data?.auto_renewing,
      color: '#f59e0b',
      loading: smartViewCounts.loading,
      onClick: () => handleSmartViewNav('auto_renewing'),
    },
    {
      lucideIcon: PenLine,
      label: t('clmDashboard.stalledSignatures', 'Stalled Signatures'),
      count: sigPerformance.data?.stalled_signatures?.length,
      color: '#3b82f6',
      loading: sigPerformance.loading,
      onClick: () => navigate('/contract?tab=pending_signature'),
    },
    {
      icon: WarningAmber,
      label: t('clmDashboard.unreviewedAlerts', 'Unreviewed Alerts'),
      count: alertSummary.data?.unreviewed,
      color: '#a855f7',
      loading: alertSummary.loading,
      onClick: () => navigate('/monitoring'),
    },
  ];

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          fontSize: '0.85rem',
          color: GOLD,
          mb: 1.5,
          letterSpacing: '0.02em',
        }}
      >
        {t('clmDashboard.actionRequired', 'Action Required')}
      </Typography>
      <Grid container spacing={2}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <ActionCard {...card} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ActionRequiredRow;
